<template>
  <v-container>
    <v-row>
      <v-col>
        <h1 class="display-1">Claims Search</h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6" lg="4" class="pb-0">
        <v-text-field
          v-model="searchTerm"
          label="Claim Number"
          autofocus
          clearable
          outlined
          @click:clear="clearSearch"
        ></v-text-field>
      </v-col>
      <v-col cols="2">
        <v-select
          :items="claimsTypes"
          v-model="claimType"
          label="Outlined style"
          outlined
        ></v-select>
      </v-col>
    </v-row>
    <v-row>
      <!-- <v-col v-if="load" cols="12" class="text-center">
        <v-progress-circular
          color="secondary"
          indeterminate
          :size="50"
          :width="3"
        ></v-progress-circular>
      </v-col> -->
      <v-col v-if="searchTerm.length === 0" cols="12">
        <v-alert :value="true" type="info" class="text-center">
          Please type a claim number to search
        </v-alert>
      </v-col>

      <v-col v-else-if="reportData.length === 0" cols="8" class="text-center">
        <v-alert :value="true" type="info">
          There is no data available for this selection. Please change your filter selections and
          run report again
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-data-table
            :headers="headers"
            :items="reportData"
            :sort-desc="true"
            :width="100"
            :loading="load"
            :footer-props="{
              'items-per-page-options': [25, 50, 75, 100, -1],
            }"
            :items-per-page="25"
            multi-sort
            return-object
            loading-text="Loading data..."
            class="elevation-0 col-md-12 m-auto"
          >
            <template v-slot:group.header="{ group, headers, isOpen, toggle, remove, groupBy }">
              <td style="background: #fff" :colspan="headers.length">
                <v-btn @click="toggle" small icon :ref="group" :data-open="isOpen">
                  <v-icon v-if="isOpen">mdi-chevron-up</v-icon>
                  <v-icon v-else>mdi-chevron-down</v-icon>
                </v-btn>
                <span class="font-weight-bold">{{
                  groupBy[0] === 'AMOUNT'
                    ? '$' + group.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
                    : group
                }}</span>
                <v-icon small @click="remove">mdi-close</v-icon>
              </td>
            </template>

            <template v-slot:item.CLAIM_ID="{ item }" v-if="claimType !== 'Prescriptions'">
              <v-chip small @click="openClaimDetails(item.CLAIM_ID)">{{ item.CLAIM_ID }}</v-chip>
            </template>

            <template v-slot:item.DETAILS="{ item }">
              <patient-modal :patient="item.SUBSCRIBER_ID" :insurance="insurance" />
            </template>

            <template slot="group.summary" slot-scope="props" style="background: #f00">
              <td style="background: #fff" :colspan="props.headers.length">
                Total Amount: ${{
                  formatCurrency(props.items.reduce((a, rec) => a + rec.AMOUNT, 0))
                }}
              </td>
            </template>

            <template slot="footer">
              <ExportCSV :data="this.csvData" :name="'claims' + '.csv'">
                <v-btn color="secondary" @click="exportData()" text small class="export-button"
                  >Export to CSV</v-btn
                >
              </ExportCSV>
            </template>
          </v-data-table>
          <ClaimDetails
            :insurance="insurance"
            :claimID="claimDetailsID"
            v-model="claimDetailsDialog"
          />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';
import ExportCSV from 'vue-json-csv';
import { debounce } from 'lodash';
import PatientModal from '../components/PatientModal.vue';
import ClaimDetails from '../components/claimDetails.vue';

export default {
  name: 'ClaimsSearch',
  components: {
    PatientModal,
    ClaimDetails,
    ExportCSV,
  },
  data() {
    return {
      searchTerm: '',
      load: false,
      insurance: '',
      selectedInsurance: '',
      reportData: [],
      claimDetailsID: '',
      claimsTypes: ['Professional', 'Institutional', 'Prescriptions'],
      claimType: 'Professional',
      user: '',
      csvData: [],
      claimDetailsDialog: false,
      headers: [],
    };
  },
  mounted() {
    this.getUser();
  },
  methods: {
    openClaimDetails(id) {
      this.claimDetailsDialog = true;
      this.claimDetailsID = id;
    },
    async getUser() {
      await axios
        .get('/api/user')
        .then((response) => {
          this.user = response.data[0];
          this.insurance = this.user.insurance[0];
        })
        .catch((err) => this.$router.push('/login'));
      this.checkPresets();
    },
    clearSearch() {
      this.searchTerm = '';
    },
    async getReport() {
      this.load = true;
      this.updateHeaders();
      const getAllData = debounce(async () => {
        // Patient data route, hardcoing the specialty param so that we pull all data for both Patients and Specialty toggle on inital render
        let url;
        if (this.user.role === 'Provider' || this.user.role === 'Group' || this.user.role === "Affiliation") {
          url = `api/claims/search/${this.insurance}/${this.claimType}/${this.user.role}/${this.user.npi}/${this.searchTerm}`;
        } else {
          url = `api/claims/search/${this.insurance}/${this.claimType}/${this.searchTerm}`;
        }
        try {
          const tableData = await axios.get(url);
          this.reportData =
            tableData.data.data !== undefined ? tableData.data.data : tableData.data;
          console.log(this.reportData);
          this.showTotals = true;
        } catch (error) {
          console.error(error);
        }
        this.load = false;
      }, 300);
      getAllData();
    },
    updateHeaders() {
      if (this.claimType === 'Prescriptions') {
        this.headers = [
          { text: 'Claim Number', value: 'RX_CLAIM_ID' },
          { text: 'PCP', value: 'PCP_NAME' },
          { text: 'Servicer Name', value: 'SERVICE_LOCATION_NAME' },
          { text: 'Patient Name', value: 'MEMBER_NAME' },
          { text: 'NDC Number', value: 'NDC_NUMBER' },
          { text: 'Service Date', value: 'SERVICE_DATE' },
          { text: 'Metric Quantity', value: 'METRIC_QUANTITY' },
          { text: 'Pharmacy Name', value: 'PHARMACY_NAME' },
          { text: 'Amount', value: 'AMOUNT' },
          { text: 'Details', value: 'DETAILS' },
        ];
      } else {
        this.headers = [
          { text: 'Claim Number', value: 'CLAIM_ID' },
          { text: 'PCP', value: 'PCP_NAME' },
          { text: 'Servicer Name', value: 'SERVICE_LOCATION_NAME' },
          { text: 'Patient Name', value: 'MEMBER_NAME' },
          { text: 'Line Number', value: 'LINE_NUMBER' },
          { text: 'Start Date', value: 'SERVICE_FROM_DATE' },
          { text: 'End Date', value: 'SERVICE_TO_DATE' },
          { text: 'Amount', value: 'AMOUNT' },
          { text: 'Status', value: 'LINE_CLAIM_STATUS' },
          { text: 'Details', value: 'DETAILS' },
          { text: 'Line EOP Description', value: 'LINE_EOP_DESC' },
          { text: 'EOP Description', value: 'EOP_DESC' },
        ];
      }
    },
  },
  watch: {
    searchTerm() {
      if (this.searchTerm !== '') {
        this.load = true;
        this.getReport();
      }
    },
    claimType() {
      this.getReport();
    },
    detailsDialog() {
      if (this.detailsDialog === false) {
        this.detailsId = '';
      }
    },
    claimDetailsDialog() {
      if (this.claimDetailsDialog === false) {
        this.claimDetailsID = '';
      }
    },
  },
};
</script>

<style scoped>
.export-button {
  position: absolute;
  left: 10px;
  bottom: 20px;
}
</style>
